import { Modal } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { X } from 'react-feather';
import { DownloadSimple } from 'phosphor-react';
import CryptoJS from 'crypto-js';
import { COLORS } from '../../../../utils/colors';
import { AppButton } from '../../../general/AppButton/AppButton';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { InvitedSignatures } from '@dill/dill-shared';
import {
  downloadFile,
  firebaseLargeFileUploadHelper,
  openDillToast,
} from '../../../../utils/helpers';
import {
  updateCreditApplication,
  getSupplierCreditApplication,
} from '../../../../redux/services/creditApplicationsService';
import { UploadFileInputs } from './UploadFileInput';

const UpdateSignatureFileModal = ({
  open,
  handleClose,
  selectedInvitedSignatureId,
  selectedInvitedSignatures,
  creditApplicationId,
}: {
  open: boolean;
  handleClose: () => void;
  selectedInvitedSignatures: InvitedSignatures | null;
  selectedInvitedSignatureId: string | null;
  creditApplicationId: string;
}) => {
  const dispatch = useAppDispatch();
  const [updatedFieldsValues, setUpdatedFieldsValues] = useState<
    { id: string; name: string; isRequired: boolean; value: any }[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<
    {
      uniqueId: string;
      errorText: string;
    }[]
  >([]);

  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);

  const handleCloseModal = () => {
    setUpdatedFieldsValues([]);
    setIsLoading(false);
    setErrors([]);
    handleClose();
  };

  const validateForm = () => {
    const fieldErrors: { uniqueId: string; errorText: string }[] = [];

    for (const field of updatedFieldsValues) {
      // Required field check
      let value = field.value;
      if (typeof value === 'string') {
        value = value.trim();
      }
      if (field.isRequired && !value) {
        fieldErrors.push({
          uniqueId: field.id,
          errorText: `${field.name} is required.`,
        });
      }
    }

    setErrors(fieldErrors);
    return fieldErrors.length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    if (selectedInvitedSignatures === null) return;
    setIsLoading(true);
    const fields = await Promise.all(
      updatedFieldsValues.map(async (field) => {
        let value = field.value;
        if (typeof field.value === 'object' && field.value instanceof File) {
          const fileType = value.name.split('.').pop() || '';
          const uploadFileUrl = await firebaseLargeFileUploadHelper({
            blobUrl: URL.createObjectURL(value),
            fileNameWithExtension: value.name,
            storagePath: `creditApplicationFiles/${currentSupplier?.id || ''}/${Date.now()}`,
          });
          value = {
            name: value.name,
            url: uploadFileUrl ?? '',
            fileType: fileType,
          };
        }
        return { ...field, value };
      })
    );

    const updatedSignatureFields = (selectedInvitedSignatures?.fields ?? []).map((field) => {
      const updatedField = fields.find((f) => f.id === field.id);
      if (field.isHashed && field.value) {
        const secretKey = process.env.REACT_APP_CRYPTO_KEY;
        if (secretKey) {
          const token = CryptoJS.AES.encrypt(JSON.stringify(field.value), secretKey).toString();
          return { ...field, value: token };
        }
      }
      return updatedField ? { ...field, value: updatedField.value } : field;
    });
    const response = await dispatch(
      updateCreditApplication({
        creditApplicationId: creditApplicationId,
        invitedSignature: { ...selectedInvitedSignatures, fields: updatedSignatureFields },
      })
    );

    if (response.type === 'creditApplications/updateCreditApplication/fulfilled') {
      openDillToast({
        message: 'Files updated successfully',
        type: 'SUCCESS',
      });
      handleCloseModal();
      dispatch(
        getSupplierCreditApplication({
          supplierId: currentSupplier?.id || '',
          creditApplicationId: creditApplicationId,
          hashedType: 'encrypted',
          fetchType: 'detailed',
          skipLoading: 'true',
        })
      );
    } else {
      openDillToast({
        message: 'Failed to update files',
        type: 'ERROR',
      });
    }
    setIsLoading(false);
  };

  const onChangeFormData = ({ id, value }: { id: string; value: any }) => {
    setUpdatedFieldsValues((prev) => prev.map((f) => (f.id === id ? { ...f, value: value } : f)));
  };

  const handleDownloadFile = async (fileUrl: string, fileName: string) => {
    downloadFile(fileUrl ?? '', fileName);
  };

  const onlyFileTypes = useMemo(() => {
    if (!selectedInvitedSignatures) return [];
    const fields =
      selectedInvitedSignatures.fields?.filter(
        (field) => field.id === selectedInvitedSignatureId
      ) || [];
    if (fields.length === 0) return [];
    setUpdatedFieldsValues(
      fields.map((field) => ({
        id: field.id,
        name: field.name,
        isRequired: field.isRequired,
        value: null,
      }))
    );
    return fields;
  }, [selectedInvitedSignatures, selectedInvitedSignatureId]);

  return (
    <Modal
      open={open}
      onClose={handleCloseModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description">
      <div
        style={{ maxHeight: '90vh' }}
        className="flex w-4/12 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg  flex-col overflow-hidden">
        <div className="sticky top-0 z-10 bg-white py-2 p-4">
          <X
            className="absolute right-2 top-2 cursor-pointer"
            color={COLORS.GREY_500}
            onClick={handleCloseModal}
          />
          <h3 className="text-lg font-bold ">Update File</h3>
        </div>
        <div className="flex flex-col mt-2 GREY_400-BORDER border-y p-4">
          {onlyFileTypes.length > 0 && (
            <div>
              <div className="flex gap-4 w-full border-b pb-1 mb-1">
                <h3 className="text-base font-semibold mb-2 w-full">{`Current File${
                  onlyFileTypes.length > 1 ? 's' : ''
                }`}</h3>
                <h3 className="text-base font-semibold mb-2 w-full">{`Upload File${
                  onlyFileTypes.length > 1 ? 's' : ''
                }`}</h3>
              </div>
              <div>
                {onlyFileTypes.map((field) => {
                  const getValue = updatedFieldsValues.find((f) => f.id === field.id)?.value;
                  return (
                    <div key={field.id} className={`flex gap-4 w-full border-b pb-1 mb-1`}>
                      <div className="mb-2 w-full">
                        <div className="mb-1 w-full text-sm">{field.name}</div>
                        {field.fromField === 'CONDITIONAL_OPTION' && (
                          <div>
                            <div className="text-sm text-gray-600">Yes</div>
                            <div className="mb-1 w-full text-sm">{field.secondaryText}</div>
                          </div>
                        )}
                        {field.value?.url && (
                          <div className="flex gap-2 items-center">
                            <AppButton
                              type="TERTIARY"
                              buttonStyles={{
                                width: '30px',
                                height: '30px',
                                borderRadius: '4px',
                                padding: '0px',
                              }}
                              onClick={(e: any) => {
                                e.preventDefault();
                                handleDownloadFile(field.value?.url ?? '', field.value?.name ?? '');
                              }}
                              icon={<DownloadSimple color={COLORS.GREY_500} size={14} />}
                            />
                            <div className="text-sm text-gray-600">{field.value?.name || ''}</div>
                          </div>
                        )}
                      </div>
                      <div className="mb-2 w-full">
                        {field.fromField === 'CONDITIONAL_OPTION' && (
                          <div>
                            <div className="mb-1 w-full text-sm">{field.name}</div>
                            <div className="text-sm text-gray-600">Yes</div>
                          </div>
                        )}
                        <UploadFileInputs
                          isRequired={field.isRequired}
                          errorText={errors.find((e) => e.uniqueId === field.id)?.errorText || ''}
                          label={field.secondaryText || field.name}
                          selectedFile={
                            typeof getValue === 'object' && getValue instanceof File
                              ? getValue
                              : null
                          }
                          onSelectFile={(data) => {
                            onChangeFormData({
                              id: field.id,
                              value: data.file,
                            });
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-between p-4 w-full">
          <AppButton
            text="Close"
            onClick={handleCloseModal}
            type="TERTIARY"
            isDisabled={isLoading}
            buttonStyles={{ width: '140px', height: '40px' }}
          />
          <AppButton
            text="Submit"
            onClick={handleSubmit}
            isDisabled={isLoading}
            isLoading={isLoading}
            buttonStyles={{ width: '140px', height: '40px' }}
          />
        </div>
      </div>
    </Modal>
  );
};

export default UpdateSignatureFileModal;
